import { ALL, OTHER, type NavOrderMode } from './constants';

export type BuildNavItemsInput = {
  savedNav?: string[];
  configuredValues?: string[];
  presets?: string[];
  typesInUse?: string[];
  hasUntyped?: boolean;
  showAllInNav?: boolean;
  orderMode?: NavOrderMode;
  topN?: number;
  computeOther?: (ctx: {
    base: string[];
    configuredValues: string[];
    typesInUse: string[];
    hasUntyped: boolean;
    topN: number;
  }) => boolean;
};

export const buildNavItems = (input: BuildNavItemsInput): string[] => {
  const {
    savedNav = [],
    configuredValues = [],
    presets = [],
    typesInUse = [],
    hasUntyped = false,
    showAllInNav = true,
    orderMode = 'ALPHA',
    topN = 6,
    computeOther,
  } = input;

  // Normalize lists: trim, drop falsy, remove reserved tokens, and dedupe while preserving order
  const normalizeList = (values: string[]): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of values) {
      const v = String(raw).trim();
      if (!v || v === ALL || v === OTHER) continue;
      if (!seen.has(v)) {
        seen.add(v);
        result.push(v);
      }
    }
    return result;
  };

  const normalizedConfigured = normalizeList(configuredValues);
  const normalizedPresets = normalizeList(presets);

  const getBase = (): string[] => {
    if (savedNav.length > 0) return [...savedNav];

    if (normalizedPresets.length > 0) {
      const filtered = normalizedConfigured.length
        ? normalizedPresets.filter((p) => normalizedConfigured.includes(p))
        : [...normalizedPresets];
      if (filtered.length > 0) return filtered;
    }

    const fallbackPool =
      normalizedConfigured.length > 0 ? normalizedConfigured : typesInUse;
    if (fallbackPool.length === 0) return [];

    const ordered =
      normalizedConfigured.length > 0 && orderMode === 'MANUAL'
        ? [...fallbackPool]
        : [...fallbackPool].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
    return ordered.slice(0, topN);
  };

  const base = getBase();
  const withAll = showAllInNav ? ([ALL, ...base] as string[]) : base;

  const defaultNeedOther = () => {
    // Compare case-insensitively to avoid false positives from casing differences
    const baseSetLc = new Set(base.map((v) => v.trim().toLowerCase()));
    const outsideSaved = typesInUse.some(
      (t) => !baseSetLc.has(String(t).trim().toLowerCase())
    );
    // Only show "Other" when there are in-use types not present in the selected nav,
    // when there are untyped contacts, or when fallback lists were truncated by topN.
    const exceededTopN =
      normalizedConfigured.length > base.length && base.length === topN;
    return hasUntyped || outsideSaved || exceededTopN;
  };

  const needOther = computeOther
    ? computeOther({
        base,
        configuredValues: normalizedConfigured,
        typesInUse,
        hasUntyped,
        topN,
      })
    : defaultNeedOther();

  // Append Other at most once
  const finalItems = needOther
    ? withAll.includes(OTHER)
      ? withAll
      : [...withAll, OTHER]
    : withAll;

  return finalItems;
};
